import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';

import { cn } from '@/components/ui/cn';
import { InlineError, StatePanel } from '@/components/ui/StatePanel';
import { plural } from '@/lib/plural';

import { formatPctEs } from '../presentation';
import type { Summary } from '../types';
import { useDashboardSummary } from '../use-dashboard';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

// Indicadores del Panel del QMS (so=430, spec §3.1; mockup 01): controlados, vigentes,
// pendientes de firma, vencidos y (G4) en capacitación, del resumen de so=410. Sin sparklines: no hay serie
// histórica (spec D3). Mientras carga, la fila con su forma real y un guion.

const DASH = '—';

interface TileProps {
  k: string;
  v: ReactNode;
  d: string;
  tone?: 'ok' | 'warn' | 'red';
  to?: string;
}

function Tile({ k, v, d, tone, to }: TileProps): ReactElement {
  const body = (
    <>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
      <div className="d">{d}</div>
    </>
  );
  const cls = cn('tile rise', tone);
  return to ? (
    <Link to={to} className={cls} data-testid="kpi">
      {body}
    </Link>
  ) : (
    <div className={cls} data-testid="kpi">
      {body}
    </div>
  );
}

function pendingDetail(p: Summary['pendingSignature']): string {
  const today = `${p.dueToday} ${p.dueToday === 1 ? 'vence' : 'vencen'} hoy`;
  return p.overdue > 0 ? `${today} · ${plural(p.overdue, 'vencida', 'vencidas')}` : today;
}

function tiles(s: Summary | undefined): ReactElement[] {
  return [
    <Tile
      key="controlled"
      k="Documentos controlados"
      v={s?.controlled.total ?? DASH}
      d={
        s ? `en ${plural(s.controlled.processCount, 'proceso', 'procesos')}` : 'en la Lista Maestra'
      }
    />,
    <Tile
      key="current"
      k="Vigentes"
      tone="ok"
      v={
        s && s.current.pct !== null ? (
          <>
            {formatPctEs(s.current.pct)}
            <small>%</small>
          </>
        ) : (
          DASH
        )
      }
      d={s ? `${s.current.upToDate} al día` : 'de los controlados'}
    />,
    <Tile
      key="pending"
      k="Pendientes de firma"
      tone="warn"
      to="/documentos?estado=por_firmar"
      v={s?.pendingSignature.total ?? DASH}
      d={s ? pendingDetail(s.pendingSignature) : 'en aprobación'}
    />,
    <Tile
      key="overdue"
      k="Vencidos / escalados"
      tone="red"
      v={s?.overdue.documents ?? DASH}
      d={s ? plural(s.overdue.escalated, 'escalado', 'escalados') : 'revisión o etapa'}
    />,
    <Tile
      key="training"
      k="En capacitación"
      to="/documentos?estado=en_capacitacion"
      v={s?.inTraining.total ?? DASH}
      d={
        s
          ? s.inTraining.overdue > 0
            ? plural(s.inTraining.overdue, 'vencida', 'vencidas')
            : 'al día'
          : 'firmados, con la capacitación abierta'
      }
    />,
  ];
}

export function KpiTiles({ companyId }: { companyId: string }): ReactElement {
  const query = useDashboardSummary({ companyId, enabled: true });
  // Sin datos, el aviso reemplaza los indicadores; si falla una recarga, se conservan.
  if (query.isError && query.data === undefined) {
    return (
      <StatePanel
        icon={<AlertTriangle size={20} />}
        title="No se pudieron cargar los indicadores"
        tone="danger"
        role="alert"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      >
        {errorAdvice(query.error)}
      </StatePanel>
    );
  }
  return (
    <>
      <div className="tiles cols-5" aria-busy={query.data === undefined}>
        {tiles(query.data)}
      </div>
      {query.isError && (
        <div className="card" style={{ marginTop: 8 }}>
          <InlineError
            icon={<RefreshCw size={16} />}
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          >
            No se pudieron actualizar los indicadores. Se muestran los anteriores.
            {refreshAdvice(query.error)}
          </InlineError>
        </div>
      )}
    </>
  );
}
